import { ChangeDetectionStrategy, Component, inject, OnInit, output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { map, Observable } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { SelectOption } from '@fcom/common';
import { ButtonTheme, IconPosition } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';

import { AmountUpdateType } from '../../interfaces/paxAmount.interface';
import { HotelRoom } from '../../interfaces/hotel.interface';

const ROOM_CONFIG = {
  maxRooms: 8,
  adults: { min: 1, max: 6 },
  children: { min: 0, max: 6 },
};
const CHILD_AGE_COUNT = 18;

interface RoomState {
  adults: number;
  children: number;
  childrenForm: UntypedFormGroup;
}

@Component({
  selector: 'fin-guests-and-rooms-selector',
  templateUrl: './guests-and-rooms-selector.component.html',
  styleUrls: ['./guests-and-rooms-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GuestsAndRoomsSelectorComponent implements OnInit {
  private languageService = inject(LanguageService);
  private fb = inject(UntypedFormBuilder);

  readonly AmountUpdateType = AmountUpdateType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly ROOM_CONFIG = ROOM_CONFIG;

  readonly setRooms = output<HotelRoom[]>();

  modalOpen = false;
  childAgeOptions$: Observable<SelectOption[]>;
  rooms: RoomState[] = [];

  ngOnInit(): void {
    this.rooms = [this.createRoom()];

    this.childAgeOptions$ = this.languageService
      .translateMultiple(['bookingWidgetTabs.hotels.guests.year', 'bookingWidgetTabs.hotels.guests.years'])
      .pipe(
        map(([year, years]) =>
          [...Array(CHILD_AGE_COUNT).keys()].map((age) => ({
            name: `${age} ${age === 1 ? year : years}`,
            value: age.toString(),
          }))
        )
      );

    this.emitSelectedRooms();
  }

  get totalAdults(): number {
    return this.rooms.reduce((total, room) => total + room.adults, 0);
  }

  get totalChildren(): number {
    return this.rooms.reduce((total, room) => total + room.children, 0);
  }

  openModal(): void {
    this.modalOpen = true;
  }

  done(): void {
    const missingAge = this.rooms.some((room) => room.childrenForm.invalid);
    if (missingAge) {
      this.rooms.forEach((room) => room.childrenForm.markAllAsTouched());
      return;
    }
    this.modalOpen = false;
  }

  addRoom(): void {
    this.rooms.push(this.createRoom());
    this.emitSelectedRooms();
  }

  removeRoom(roomIndex: number): void {
    this.rooms = [...this.rooms.slice(0, roomIndex), ...this.rooms.slice(roomIndex + 1)];
    this.emitSelectedRooms();
  }

  onAdultsUpdate(updateType: AmountUpdateType, roomIndex: number): void {
    this.rooms[roomIndex].adults += updateType === AmountUpdateType.INCREMENT ? 1 : -1;
    this.emitSelectedRooms();
  }

  onChildrenUpdate(updateType: AmountUpdateType, roomIndex: number): void {
    const room = this.rooms[roomIndex];

    if (updateType === AmountUpdateType.INCREMENT) {
      room.childrenForm.addControl(`child-${room.children}`, this.fb.control('', Validators.required));
      room.children += 1;
    } else {
      room.children -= 1;
      room.childrenForm.removeControl(`child-${room.children}`);
    }

    this.emitSelectedRooms();
  }

  emitSelectedRooms(): void {
    this.setRooms.emit(
      this.rooms.map((room) => ({
        adults: room.adults,
        childAges: Object.values(room.childrenForm.controls).map((control) =>
          control.value === '' || control.value == null ? null : Number(control.value)
        ),
      }))
    );
  }

  private createRoom(): RoomState {
    return { adults: 1, children: 0, childrenForm: this.fb.group({}) };
  }
}
